/* eslint-disable jira/react-no-inline-function-prop */
/** @jsx jsx */
import React, { useCallback, useState, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import { useCrossFlow, Targets } from '@atlassiansox/cross-flow-support';
import { IconButton } from '@atlaskit/button/new';
import Button from '@atlaskit/button';
import {
	SpotlightManager,
	SpotlightTarget,
	SpotlightTransition,
	SpotlightPulse,
} from '@atlaskit/onboarding';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	DRAWER,
} from '@atlassian/jira-product-analytics-bridge';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getProjectName, projectIdSelector } from '../../../state/selectors/app/index.tsx';
import { useCoUseFlow } from '../co-use-flow/index.tsx';
import {
	ELEMENT_IDS,
	USER_PROPERTY_KEY_NAMES,
	CTA_SOURCE_CONTEXT,
	CTA_SOURCE_COMPONENT,
	CTA_SPOTLIGHT_SOURCE_CONTEXT,
	type Flow,
	setHasClickedOnPulse,
	getHasClickedOnPulse,
} from '../constants.tsx';
import { onClickCTA } from '../on-click-cta/utils.tsx';
import { useIsBacklogCardListSizeGreaterThanTwo } from '../utils.tsx';

import messages from '../messages.tsx';
import { AdControls } from './ad-controls/view.tsx';
import { useAdControls } from './ad-controls/controller.tsx';
import { entryPoint } from './animation/entrypoint.tsx';

type BacklogButtonRefreshProps = {
	flow: Flow;
	hoverPulse: boolean;
};

export const BacklogButtonRefresh = ({ flow, hoverPulse }: BacklogButtonRefreshProps) => {
	const coUseEligible = flow === 'coUse';
	const crossFlowEligible = flow === 'crossFlow';
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireInteractedUIEvent, fireViewedScreenEvent } = useFireCrossFlowAnalyticsEvents();
	const crossflow = useCrossFlow();
	const { hasDismissed, dismiss } = useAdControls();

	const url = useCoUseFlow(coUseEligible);

	// Extract project name and project id
	const projectName = useBacklogSelector(getProjectName);
	const projectId = useBacklogSelector(projectIdSelector);
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(entryPoint, {});

	const onPlanOnWhiteboard = useCallback(
		(actionSubjectId: string) => {
			if (coUseEligible) {
				setHasClickedOnPulse();
			}
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				actionSubjectId,
				{
					experiment: 'backlogButtonRefresh',
					isCrossFlowEligible: crossFlowEligible,
					isCoUseEligible: coUseEligible,
				},
			);

			// UCIA event
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: CTA_SOURCE_CONTEXT,
				},
				{
					targetProductId: Targets.CONFLUENCE,
					recommendedProductIds: [Targets.CONFLUENCE],
					source: CTA_SOURCE_COMPONENT,
				},
			);

			onClickCTA({
				createAnalyticsEvent,
				crossFlowEligible,
				crossflow,
				projectId,
				projectName,
				component: CTA_SOURCE_COMPONENT,
				context: CTA_SOURCE_CONTEXT,
				coUseEligible,
				url,
			});
		},
		[
			crossFlowEligible,
			coUseEligible,
			projectId,
			projectName,
			crossflow,
			createAnalyticsEvent,
			fireInteractedUIEvent,
			url,
		],
	);

	const handleDismissEntrypoint = useCallback(async () => {
		await dismiss(USER_PROPERTY_KEY_NAMES.WHITEBOARDS_BACKLOG_ENTRYPOINT_DISMISSED);
	}, [dismiss]);

	const openSpotlight = useCallback(() => {
		entryPointActions.load();
		setIsSpotlightActive(true);
	}, [entryPointActions]);

	const closeSpotlight = useCallback(() => {
		entryPointActions.unload();
		setIsSpotlightActive(false);
	}, [entryPointActions]);

	const hasClickedOnPulse = getHasClickedOnPulse();

	const shouldRenderPulse =
		(useIsBacklogCardListSizeGreaterThanTwo() &&
			coUseEligible &&
			!isSpotlightActive &&
			!hasClickedOnPulse) ||
		(crossFlowEligible && hoverPulse);

	useEffect(() => {
		if (!hasDismissed) {
			// UCIA event
			fireViewedScreenEvent('planOnWhiteboardsButtonCTAScreen', {
				recommendedProductIds: [Targets.CONFLUENCE],
			});
		}
	}, [hasDismissed, fireViewedScreenEvent]);

	if (hasDismissed) return null;

	const footer = () => (
		<AdControls isCta onDismissAd={handleDismissEntrypoint}>
			<Button
				appearance="default"
				onClick={() => onPlanOnWhiteboard(CTA_SPOTLIGHT_SOURCE_CONTEXT)}
				id={ELEMENT_IDS.CTA_BUTTON}
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				css={buttonStyles}
			>
				{formatMessage(messages.spotlightCta)}
			</Button>
		</AdControls>
	);

	return (
		<Box>
			<SpotlightManager blanketIsTinted={false} onBlanketClicked={closeSpotlight}>
				<SpotlightTarget name="whiteboards">
					<SpotlightPulse
						radius={3}
						pulse={shouldRenderPulse}
						testId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.pulse"
					>
						<IconButton
							id={ELEMENT_IDS.TOUCHPOINT}
							icon={WhiteboardIcon}
							onClick={() => onPlanOnWhiteboard(CTA_SOURCE_CONTEXT)}
							spacing="compact"
							label={formatMessage(messages.planOnWhiteboard)}
							onMouseEnter={crossFlowEligible ? openSpotlight : undefined}
							isTooltipDisabled={crossFlowEligible}
						/>
					</SpotlightPulse>
				</SpotlightTarget>

				<SpotlightTransition>
					{isSpotlightActive && (
						<ContextualAnalyticsData sourceName="planOnWhiteboardSpotlight" sourceType={DRAWER}>
							<JiraSpotlight
								actions={[
									{
										onClick: () => onPlanOnWhiteboard(CTA_SPOTLIGHT_SOURCE_CONTEXT),
										text: formatMessage(messages.spotlightCta),
									},
								]}
								target="whiteboards"
								targetRadius={3}
								dialogWidth={320}
								dialogPlacement="bottom right"
								pulse={false}
								targetOnClick={() => onPlanOnWhiteboard(CTA_SOURCE_CONTEXT)}
								messageType="engagement"
								messageId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.jira-spotlight.whiteboards"
								testId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.whiteboards-spotlight"
								key="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.jira-spotlight.whiteboards"
								footer={footer}
							>
								<Box xcss={styles}>
									<JiraEntryPointContainer
										entryPointReferenceSubject={entryPointReferenceSubject}
										id="whiteboards-entrypoint-backlog-button-refresh-animation"
										runtimeProps={{}}
									/>
									<Box padding="space.200">
										<Heading size="medium">{formatMessage(messages.spotlightHeading)}</Heading>
										<Text>{formatMessage(messages.spotlightContent)}</Text>
									</Box>
								</Box>
							</JiraSpotlight>
							<FireScreenAnalytics
								attributes={{
									crossFlowEligible,
									coUseEligible,
									experiment: 'backlogButtonRefresh',
								}}
							/>
						</ContextualAnalyticsData>
					)}
				</SpotlightTransition>
			</SpotlightManager>
			<FireScreenAnalytics
				attributes={{ crossFlowEligible, coUseEligible, experiment: 'backlogButtonRefresh' }}
			/>
		</Box>
	);
};

const buttonStyles = css({
	/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles */
	color: `${token('color.text.inverse')} !important`,
	backgroundColor: token('color.background.inverse.subtle'),
	'&:hover': {
		/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles */
		color: `${token('color.text.inverse')} !important`,
	},
});

/**
 * Unfortunately there is no way to alter the padding of the spotlight content other than adding negative margins.
 */
const styles = xcss({
	marginTop: 'space.negative.250',
	marginRight: 'space.negative.250',
	marginBottom: 'space.negative.250',
	marginLeft: 'space.negative.250',
});
